import { supabase } from './supabase';

export interface ChristianTip {
  id: string;
  type: 'livro' | 'filme';
  title: string;
  description: string;
  cover_image: string;
  link: string | null;
  age_rating: 'Livre' | '10+' | '12+' | '14+' | '16+' | '18+';
  published_at: string;
  status: 'ativo' | 'inativo';
  created_at: string;
  updated_at: string;
  user_id: string;
}

export type ChristianTipInput = Omit<ChristianTip, 'id' | 'created_at' | 'updated_at' | 'user_id'>;

/**
 * Busca todas as dicas cristãs ativas
 */
export async function fetchChristianTips(
  options: {
    type?: 'livro' | 'filme';
    ageRating?: string;
    page?: number;
    perPage?: number;
    orderBy?: 'published_at' | 'title';
    orderDirection?: 'asc' | 'desc';
  } = {}
): Promise<{ data: ChristianTip[]; count: number }> {
  try {
    const {
      type,
      ageRating,
      page = 1,
      perPage = 12,
      orderBy = 'published_at',
      orderDirection = 'desc'
    } = options;

    // Iniciar a consulta
    let query = supabase
      .from('christian_tips')
      .select('*', { count: 'exact' })
      .eq('status', 'ativo');

    // Aplicar filtros
    if (type) {
      query = query.eq('type', type);
    }

    if (ageRating) {
      query = query.eq('age_rating', ageRating);
    }

    // Aplicar ordenação
    query = query.order(orderBy, { ascending: orderDirection === 'asc' });

    // Aplicar paginação
    const from = (page - 1) * perPage;
    const to = from + perPage - 1;
    query = query.range(from, to);

    // Executar a consulta
    const { data, error, count } = await query;

    if (error) throw error;
    return { data: data || [], count: count || 0 };
  } catch (error) {
    console.error('Error fetching christian tips:', error);
    return { data: [], count: 0 };
  }
}

/**
 * Busca todas as dicas cristãs (incluindo inativas) para o painel admin
 */
export async function fetchAllChristianTips(
  options: {
    type?: 'livro' | 'filme';
    status?: 'ativo' | 'inativo';
    page?: number;
    perPage?: number;
    orderBy?: 'published_at' | 'title' | 'status';
    orderDirection?: 'asc' | 'desc';
  } = {}
): Promise<{ data: ChristianTip[]; count: number }> {
  try {
    const {
      type,
      status,
      page = 1,
      perPage = 10,
      orderBy = 'published_at',
      orderDirection = 'desc'
    } = options;

    // Iniciar a consulta
    let query = supabase
      .from('christian_tips')
      .select('*', { count: 'exact' });

    // Aplicar filtros
    if (type) {
      query = query.eq('type', type);
    }

    if (status) {
      query = query.eq('status', status);
    }

    // Aplicar ordenação
    query = query.order(orderBy, { ascending: orderDirection === 'asc' });

    // Aplicar paginação
    const from = (page - 1) * perPage;
    const to = from + perPage - 1;
    query = query.range(from, to);

    // Executar a consulta
    const { data, error, count } = await query;

    if (error) throw error;
    return { data: data || [], count: count || 0 };
  } catch (error) {
    console.error('Error fetching all christian tips:', error);
    return { data: [], count: 0 };
  }
}

/**
 * Busca uma dica cristã pelo ID
 */
export async function getChristianTipById(id: string): Promise<ChristianTip | null> {
  try {
    const { data, error } = await supabase
      .from('christian_tips')
      .select('*')
      .eq('id', id)
      .single();

    if (error) throw error;
    return data;
  } catch (error) {
    console.error('Error fetching christian tip:', error);
    return null;
  }
}

/**
 * Cria uma nova dica cristã
 */
export async function createChristianTip(tip: ChristianTipInput): Promise<ChristianTip | null> {
  try {
    const { data: { session } } = await supabase.auth.getSession();
    if (!session) throw new Error('Usuário não autenticado');

    const { data, error } = await supabase
      .from('christian_tips')
      .insert({
        ...tip,
        user_id: session.user.id
      })
      .select()
      .single();

    if (error) throw error;
    return data;
  } catch (error) {
    console.error('Error creating christian tip:', error);
    return null;
  }
}

/**
 * Atualiza uma dica cristã existente
 */
export async function updateChristianTip(id: string, tip: Partial<ChristianTipInput>): Promise<ChristianTip | null> {
  try {
    const { data, error } = await supabase
      .from('christian_tips')
      .update(tip)
      .eq('id', id)
      .select()
      .single();

    if (error) throw error;
    return data;
  } catch (error) {
    console.error('Error updating christian tip:', error);
    return null;
  }
}

/**
 * Exclui uma dica cristã
 */
export async function deleteChristianTip(id: string): Promise<boolean> {
  try {
    const { error } = await supabase
      .from('christian_tips')
      .delete()
      .eq('id', id);

    if (error) throw error;
    return true;
  } catch (error) {
    console.error('Error deleting christian tip:', error);
    return false;
  }
}

/**
 * Altera o status de uma dica cristã
 */
export async function toggleChristianTipStatus(id: string, status: 'ativo' | 'inativo'): Promise<boolean> {
  try {
    const { error } = await supabase
      .from('christian_tips')
      .update({ status })
      .eq('id', id);

    if (error) throw error;
    return true;
  } catch (error) {
    console.error('Error toggling christian tip status:', error);
    return false;
  }
}

/**
 * Valida uma URL
 */
export function isValidUrl(url: string): boolean {
  try {
    new URL(url);
    return true;
  } catch (error) {
    return false;
  }
}

/**
 * Valida uma imagem de capa
 */
export function isValidCoverImage(url: string): boolean {
  if (!isValidUrl(url)) return false;
  
  // Verifica se a URL termina com uma extensão de imagem comum
  const imageExtensions = ['.jpg', '.jpeg', '.png', '.gif', '.webp'];
  const lowercaseUrl = url.toLowerCase();
  
  return imageExtensions.some(ext => lowercaseUrl.endsWith(ext)) || 
         lowercaseUrl.includes('unsplash.com') || 
         lowercaseUrl.includes('images.pexels.com') ||
         lowercaseUrl.includes('cloudinary.com');
}

/**
 * Retorna as opções de classificação etária
 */
export function getAgeRatingOptions(): { value: string; label: string }[] {
  return [
    { value: 'Livre', label: 'Livre' },
    { value: '10+', label: '10+' },
    { value: '12+', label: '12+' },
    { value: '14+', label: '14+' },
    { value: '16+', label: '16+' },
    { value: '18+', label: '18+' }
  ];
}